<script setup lang="ts">
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const profileStore = useProfileStore();
const groupStore = useGroupStore();
const userStore = useUserStore();
const { setDocumentTitle } = useSetDocumentTitle();
const { trackEvent } = useGtm();

const isOpenSidebar = ref(false);
const isShowGoogleLoginFailDialog = ref(false);
const followBtnRef = ref();

const userId = computed(() => Number(route.params?.id));
const userSocialLink = computed(() => profileStore.socialLink);
const userName = computed(() => userStore.user?.name);
const myProfileId = computed(() => profileStore.profile?.id);
const isLoading = computed(() => userStore.userData.isLoading || groupStore.isLoading);

const sharedUserUrl = computed(() => {
	return getFullUrlPath(`/user/${userId.value}`);
});

function handleSideDrawerClose() {
	isOpenSidebar.value = false;
}

function handleOnSignIn() {
	isOpenSidebar.value = true;
}

async function updateFollowingStatus() {
	isOpenSidebar.value = false;
}

async function handleFollowGroup() {
	await followBtnRef.value?.handleFollowGroup();
	isOpenSidebar.value = false;
}

async function fetchData() {
	const apiGroup = [
		userStore.getUserById(userId.value),
		profileStore.getSocialProfile(userId.value),
	];

	await Promise.all(apiGroup);
}

function handleGoogleLoginFailed() {
	isShowGoogleLoginFailDialog.value = true;
}

watch(() => myProfileId.value, (newId) => {
	if (newId === userId.value) {
		router.push({
			name: ROUTE.PROFILE.NAME,
		});
	}
});

onBeforeMount(() => {
	if (userId.value === myProfileId.value) {
		router.push({
			name: ROUTE.PROFILE.NAME,
		});
	}
});

onMounted(async () => {
	if (userId.value !== myProfileId.value) {
		await fetchData();
		setDocumentTitle(route.name, userName.value);
	}

	trackEvent({
		user_id: userId.value,
		event: GTM_EVENT_NAME.VIEW_USER_PROFILE_SUCCESS,
	});
});
</script>

<template>
  <div class="user-profile-page-container page">
    <UserProfileHeader
      :name="userName"
      :profile-image-url="userStore.user?.avatarLargeUrl"
      :is-loading="isLoading"
    >
      <template #lowerDetail>
        <UserProfileInfoSkeletonLoader
          v-if="isLoading"
          :action-count="1"
          show-socials
        />
        <template v-else>
          <p
            v-if="userStore.user?.description"
            class="text-md text-regular"
          >
            {{ userStore.user.description }}
          </p>
          <SocialList :links="userSocialLink" />
          <div class="btn-container">
            <ShareButton
              :url="sharedUserUrl"
              :user-id="userId"
              :gtm-event-name="GTM_EVENT_NAME.SHARE_PROFILE_SUCCESS"
            />
          </div>
        </template>
      </template>
    </UserProfileHeader>

    <Divider />

    <div class="tabs">
      <GroupList
        :user-id="userId"
        :profile-id="myProfileId"
        :is-loading="isLoading"
        @on-update-following-status="updateFollowingStatus"
        @on-open-authentication-dlg="handleOnSignIn"
      />
    </div>
    <AuthenticationSideDrawerForm
      :is-open="isOpenSidebar"
      :is-loading="authStore.isLoading"
      :redirect-ids="{ userId }"
      :page="AUTH_PAGE_ORIGIN.PROFILE"
      @on-close="handleSideDrawerClose"
      @on-cancel="handleSideDrawerClose"
      @on-success="handleFollowGroup"
      @on-google-login-failed="handleGoogleLoginFailed"
    />
    <GoogleLoginFailByInAppBrowserDialog
      :is-show="isShowGoogleLoginFailDialog"
      @on-close="isShowGoogleLoginFailDialog = false"
      @on-confirm="isShowGoogleLoginFailDialog = false"
    />
  </div>
</template>

<style lang="scss" scoped>
.user-profile-page-container {
	margin: auto;
	padding-top: 0;
}

.btn-container {
	display: flex;
	align-items: center;
	column-gap: spacings-get(4);
	margin: auto;
}

.follow-btn {
	width: rem(93);
}

.tabs {
	@include media-query-max(mobile) {
		padding: 0 spacings-get(4);
	}
}
</style>